import React, { useEffect, useState, useContext } from 'react';
import { Table, Button, Modal, Typography, message, Space, Tag, Input, Form, Select, InputNumber } from 'antd';
import { EyeOutlined, FileAddOutlined, ReloadOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../../api/axios';
import { AuthContext } from '../../context/AuthContext';

const { Title, Text } = Typography;
const { Option } = Select;

const AllProformaList = () => {
  const { user } = useContext(AuthContext);
  const [proformas, setProformas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState([]);
  const [viewModal, setViewModal] = useState(false);
  const [saleModal, setSaleModal] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [banks, setBanks] = useState([]);
  const [paymentType, setPaymentType] = useState('cash');

  const userShopId = user?.shopId;
  const userWareId = user?.warehouseId;

  useEffect(() => {
    fetchProformas();
    fetchBanks();
  }, []);

  const fetchProformas = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/proformas/all');
      setProformas(res.data);
    } catch (err) {
      console.error(err);
      messageApi.error('Failed to load proformas');
    } finally {
      setLoading(false);
    }
  };

  const fetchBanks = async () => {
    try {
      const res = await api.get('/api/sales/banks'); // your bank API
      setBanks(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleView = async (record) => {
    try {
      const res = await api.get(`/api/proformas/${record.id}`);
      setSelected(record);
      const formatted = (res.data.items || []).map(item => ({
        ...item,
        importerId: item.importerId || item.sparepart?.importerId || '-',
        partName: item.partName || item.sparepart?.partName || '-',
        partNumber: item.partNumber || item.sparepart?.partNumber || '-',
      }));
      setItems(formatted);
      setViewModal(true);
    } catch (err) {
      console.error(err);
      messageApi.error('Failed to load proforma items');
    }
  };

  const handleItemUpdate = async (updatedItem) => {
    try {
      // Send updated item to the API
      const res = await api.put(`/api/proformas/items/${updatedItem.id}`, updatedItem);
      if (res.status === 200) {
        messageApi.success('Item updated successfully');
        // Update the local state with the updated item
        const updatedItems = items.map(item => 
          item.id === updatedItem.id ? updatedItem : item
        );
        setItems(updatedItems);
      }
    } catch (err) {
      console.error(err);
      messageApi.error('Failed to update item');
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      const res = await api.delete(`/api/proformas/items/${itemId}`);
      if (res.status === 200) {
        messageApi.success('Item removed successfully');
        setItems(items.filter(item => item.id !== itemId));
      }
    } catch (err) {
      console.error(err);
      messageApi.error('Failed to remove item');
    }
  };

  const handleSubmitSale = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        proformaId: selected.id,
        shopId: userShopId,
        warehouseId: userWareId,
        refNo: values.refNo,
        fsNo: values.fsNo,
        customerName: selected.customerName,
        customerTin: selected.customerTin,
        subtotal: selected.subtotal,
        paymentType: values.paymentType,
        cashAmount: values.cashAmount || 0,
        creditAmount: values.creditAmount || 0,
        bank1Id: values.bank1Id || null,
        bank1Amount: values.bank1Amount || 0,
        bank2Id: values.bank2Id || null,
        bank2Amount: values.bank2Amount || 0,
        items: items.map(i => ({
          sparepartId: i.sparepartId || i.sparepart?.id,
          partName: i.partName,
          partNumber: i.partNumber,
          importerId: i.importerId,
          quantity: i.quantity,
          unitPrice: i.unitPrice,
          lineTotal: parseFloat(i.quantity) * parseFloat(i.unitPrice)
        })),
      };

      await api.post('/api/sales/createfromproforma', payload);
      messageApi.success('Sale created successfully from proforma!');
      setSaleModal(false);
      form.resetFields();
      fetchProformas();
    } catch (err) {
      console.error(err);
      messageApi.error('Error creating sale.');
    }
  };

  const columns = [
    { title: 'Proforma No', dataIndex: 'proformaNo', key: 'proformaNo', render: t => <Text strong>{t}</Text> },
    { title: 'Customer Name', dataIndex: 'customerName', key: 'customerName', render: t => <Text strong>{t}</Text> },
    { title: 'Customer TIN', dataIndex: 'customerTin', key: 'customerTin', render: t => <Text strong>{t}</Text> },
    { title: 'Subtotal', dataIndex: 'subtotal', key: 'subtotal' },
    { title: 'Date', dataIndex: 'createdAt', key: 'createdAt', render: val => new Date(val).toLocaleString() },
    { title: 'Status', dataIndex: 'is_Active', render: val => <Tag color={val === 1 ? 'green' : 'blue'}>{val === 1 ? 'Active' : 'Pending'}</Tag> },
    {
      title: 'Actions', key: 'actions', render: (_, record) => (
        <Space>
          <Button icon={<EyeOutlined />} size="small" onClick={() => handleView(record)}>View</Button>
          {record.is_Active != 1 && (
            <Button icon={<FileAddOutlined />} size="small" type="primary" onClick={() => openCreateSale(record)}>Create Sale</Button>
          )}
        </Space>
      )
    }
  ];

  const itemColumns = [
    { title: 'Importer ID', dataIndex: 'importerId', key: 'importerId' },
    { title: 'Part Name', dataIndex: 'partName', key: 'partName' },
    { title: 'Part Number', dataIndex: 'partNumber', key: 'partNumber' },
    { title: 'Qty', dataIndex: 'quantity', key: 'quantity', align: 'right', render: (value, record) => (
      <InputNumber 
        value={value} 
        min={1} 
        onChange={(val) => {
          const updatedItem = { ...record, quantity: val };
          handleItemUpdate(updatedItem);
        }} 
      />
    ) },
    { title: 'Unit Price', dataIndex: 'unitPrice', key: 'unitPrice', align: 'right', render: (value, record) => (
      <InputNumber 
        value={value} 
        min={0} 
        onChange={(val) => {
          const updatedItem = { ...record, unitPrice: val };
          handleItemUpdate(updatedItem);
        }} 
      />
    ) },
    { title: 'Total', key: 'total', align: 'right', render: (_, r) => r.unitPrice * r.quantity },
    { title: 'Actions', key: 'actions', render: (_, record) => (
      <Button 
        icon={<DeleteOutlined />} 
        size="small" 
        danger 
        onClick={() => handleRemoveItem(record.id)}
      >
        Remove
      </Button>
    ) }
  ];

  return (
    <div style={{ padding: 24 }}>
      {contextHolder}
      <Title level={3}>All Proformas</Title>

      <Button type="default" icon={<ReloadOutlined />} onClick={fetchProformas} style={{ marginBottom: 16 }}>Refresh</Button>

      <Table bordered dataSource={proformas} columns={columns} loading={loading} rowKey="id" pagination={{ pageSize: 8 }} />

      {/* View Modal */}
      <Modal open={viewModal} title={`Proforma: ${selected?.proformaNo || ''}`} onCancel={() => setViewModal(false)} footer={<Button onClick={() => setViewModal(false)}>Close</Button>} width={850}>
        {selected && <>
          <p><strong>Date:</strong> {new Date(selected.createdAt).toLocaleString()}</p>
          <Table dataSource={items} columns={itemColumns} size="small" rowKey="id" pagination={false} />
          <div style={{ textAlign: 'right', marginTop: 10 }}><Text strong>Total: {selected.subtotal}</Text></div>
        </>}
      </Modal>

      {/* Create Sale Modal */}
      <Modal open={saleModal} title={`Create Sale from ${selected?.proformaNo}`} onCancel={() => setSaleModal(false)} onOk={handleSubmitSale} okText="Submit Sale" width={700}>
        <Form layout="vertical" form={form}>
          <Form.Item label="Reference No" name="refNo" rules={[{ required: true, message: 'Please enter Reference No' }]}><Input placeholder="Enter Reference Number" /></Form.Item>
          <Form.Item label="FS No" name="fsNo" rules={[{ required: true, message: 'Please enter FS No' }]}><Input placeholder="Enter FS Number" /></Form.Item>

          <Form.Item label="Payment Type" name="paymentType" initialValue="cash" rules={[{ required: true }]}><Select onChange={value => setPaymentType(value)}>
            <Option value="cash">Cash</Option>
            <Option value="credit">Credit</Option>
            <Option value="bank">Bank</Option>
            <Option value="mixed">Mixed</Option>
          </Select></Form.Item>

          {(paymentType === 'cash' || paymentType === 'mixed') && (
            <Form.Item label="Cash Amount" name="cashAmount">
              <InputNumber min={0} style={{ width: '100%' }} placeholder="Enter Cash Amount" />
            </Form.Item>
          )}

          {(paymentType === 'credit' || paymentType === 'mixed') && (
            <Form.Item label="Credit Amount" name="creditAmount">
              <InputNumber min={0} style={{ width: '100%' }} placeholder="Enter Credit Amount" />
            </Form.Item>
          )}

          {(paymentType === 'bank' || paymentType === 'mixed') && (
            <>
              <Form.Item label="Bank 1" name="bank1Id">
                <Select placeholder="Select Bank">
                  {banks.map(b => <Option key={b.id} value={b.id}>{b.name}</Option>)}
                </Select>
              </Form.Item>
              <Form.Item label="Bank 1 Amount" name="bank1Amount">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Enter Bank 1 Amount" />
              </Form.Item>
              <Form.Item label="Bank 2" name="bank2Id">
                <Select placeholder="Select Bank">
                  {banks.map(b => <Option key={b.id} value={b.id}>{b.name}</Option>)}
                </Select>
              </Form.Item>
              <Form.Item label="Bank 2 Amount" name="bank2Amount">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Enter Bank 2 Amount" />
              </Form.Item>
            </>
          )}
        </Form>

        <div style={{ marginTop: 20 }}>
          <Table dataSource={items} columns={itemColumns} size="small" rowKey="id" pagination={false} />
        </div>
      </Modal>
    </div>
  );
};

export default AllProformaList;
